import { useState } from "react";
import { Mail } from "lucide-react";
import Reveal from "@/components/Reveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Newsletter subscription block — used at the bottom of articles and
 * standalone on `/newsletter` (future). Includes an invisible honeypot
 * (`website` field) to trap bots without a captcha.
 */
const NewsletterSubscribe = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error | already
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch(`${BACKEND}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Subscription failed (${res.status})`);
      }
      const j = await res.json();
      setStatus(j.already_subscribed ? "already" : "success");
      setForm({ name: "", company: "", email: "", website: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      className="wrap"
      style={{
        paddingBlock: "var(--section)",
        borderTop: "1px solid var(--line)",
        background: "var(--bg-soft, var(--bg))",
      }}
      data-testid="newsletter-subscribe"
    >
      <div style={{ maxWidth: "720px", margin: "0 auto" }}>
        <Reveal>
          <Mail
            size={32}
            color="var(--brass-deep)"
            strokeWidth={1.3}
            aria-hidden="true"
            style={{ marginBottom: "1rem" }}
          />
        </Reveal>
        <Reveal as="p" className="eyebrow">
          Stay updated with industry insights
        </Reveal>
        <Reveal as="h2" className="display" style={{ margin: "1rem 0 1.2rem" }}>
          Monthly sourcing updates, market trends, and supply-chain insights.
        </Reveal>
        <Reveal
          as="p"
          style={{
            color: "var(--ink-soft)",
            fontSize: "1.05rem",
            lineHeight: 1.65,
            marginBottom: "2rem",
            maxWidth: "60ch",
          }}
        >
          Receive monthly sourcing updates, market trends, product innovations, and supply-chain insights directly from Asia Sourcing India.
        </Reveal>

        {status === "success" ? (
          <Reveal
            as="div"
            data-testid="newsletter-success"
            style={{
              padding: "1.6rem",
              border: "1px solid var(--brass-deep)",
              color: "var(--ink)",
              lineHeight: 1.6,
            }}
          >
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 .7rem" }}>
              Subscribed ✓
            </p>
            <p style={{ margin: 0, fontFamily: "var(--display)", fontSize: "1.2rem" }}>
              Thank you — a confirmation is on its way to your inbox.
            </p>
          </Reveal>
        ) : status === "already" ? (
          <Reveal
            as="div"
            data-testid="newsletter-already"
            style={{
              padding: "1.6rem",
              border: "1px solid var(--line)",
              lineHeight: 1.6,
            }}
          >
            <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 .7rem" }}>
              Already on the list
            </p>
            <p style={{ margin: 0, fontFamily: "var(--display)", fontSize: "1.15rem", color: "var(--ink-soft)" }}>
              You&apos;re already subscribed with this email — see you in the next issue.
            </p>
          </Reveal>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="newsletter-form"
            noValidate
            style={{ display: "grid", gap: "1rem" }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
              <label style={{ display: "grid", gap: ".35rem" }}>
                <span className="mono" style={{ fontSize: ".64rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase" }}>Name</span>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={onChange}
                  data-testid="newsletter-name"
                  autoComplete="name"
                  style={{
                    padding: ".8rem 1rem",
                    border: "1px solid var(--line)",
                    background: "transparent",
                    color: "inherit",
                    fontSize: "1rem",
                    fontFamily: "var(--body)",
                  }}
                />
              </label>
              <label style={{ display: "grid", gap: ".35rem" }}>
                <span className="mono" style={{ fontSize: ".64rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase" }}>Company</span>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  data-testid="newsletter-company"
                  autoComplete="organization"
                  style={{
                    padding: ".8rem 1rem",
                    border: "1px solid var(--line)",
                    background: "transparent",
                    color: "inherit",
                    fontSize: "1rem",
                    fontFamily: "var(--body)",
                  }}
                />
              </label>
            </div>
            <label style={{ display: "grid", gap: ".35rem" }}>
              <span className="mono" style={{ fontSize: ".64rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase" }}>Email</span>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={onChange}
                data-testid="newsletter-email"
                autoComplete="email"
                style={{
                  padding: ".8rem 1rem",
                  border: "1px solid var(--line)",
                  background: "transparent",
                  color: "inherit",
                  fontSize: "1rem",
                  fontFamily: "var(--body)",
                }}
              />
            </label>
            {/* Honeypot — visually hidden but still in the DOM for bots to fill */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label>
                Website
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={onChange}
                />
              </label>
            </div>

            {status === "error" && (
              <p
                role="alert"
                data-testid="newsletter-error"
                style={{ color: "#b25d3f", fontSize: ".95rem", margin: 0 }}
              >
                {errorMsg}
              </p>
            )}

            <div>
              <button
                type="submit"
                className="btn btn-fill btn-lg btn-glow"
                disabled={status === "loading"}
                data-testid="newsletter-submit"
                style={{ opacity: status === "loading" ? 0.6 : 1 }}
              >
                {status === "loading" ? "Subscribing…" : "Subscribe"}
              </button>
            </div>
            <p className="mono" style={{ fontSize: ".62rem", color: "var(--ink-soft)", letterSpacing: ".08em", textTransform: "uppercase", margin: 0 }}>
              We respect your inbox — one email per month, unsubscribe any time.
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export default NewsletterSubscribe;
